function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import withStyles from 'isomorphic-style-loader/withStyles';
import { FormattedMessage } from 'react-intl';
import cn from 'classnames';
import forEach from 'lodash/forEach';
import map from 'lodash/map';
import find from 'lodash/find';
import omit from 'lodash/omit';
import filter from 'lodash/filter';
import minBy from 'lodash/minBy';
import maxBy from 'lodash/maxBy';
import first from 'lodash/first';
import { isManualReading } from 'helpers/externalDataSources';
import { calculateRelatedValue } from 'helpers/relatedData';
import messages from '../../../messages';
import Marker from '../Marker';
import { ZINDEX_ORDER } from '../constants';
import { Labels, Summaries } from './Annotations';
import styles from './DailyChart.pcss';
var _React_PureComponent;
class DailyChart extends (_React_PureComponent = React.PureComponent) {
    static getDerivedStateFromProps(props) {
        const { readingsWithRelatedData, translatedTimeSeriesResources, startDayTimestamp } = props;
        const weekDays = Array.from({
            length: 7
        });
        const translateMomentDate = (momentDate)=>momentDate.utc().locale('en--account').startOf('day').format('X');
        const rows = new Map();
        forEach(weekDays, (_, i)=>{
            const rowTimestamp = +translateMomentDate(moment.unix(Number(startDayTimestamp)).add(i, 'days'));
            rows.set(rowTimestamp, []);
        });
        forEach(readingsWithRelatedData, (reading)=>{
            var _rows_get;
            const rowMomentDate = moment.unix(reading.timestamp);
            const rowTimestamp = +translateMomentDate(rowMomentDate);
            if (!rows.has(rowTimestamp)) {
                rows.set(rowTimestamp, []);
            }
            (_rows_get = rows.get(rowTimestamp)) === null || _rows_get === void 0 ? void 0 : _rows_get.push(reading);
        });
        forEach(translatedTimeSeriesResources, (translatedTimeSeriesResource)=>{
            var _rows_get;
            const rowMomentDate = moment.unix(translatedTimeSeriesResource.timestamp);
            const rowTimestamp = +translateMomentDate(rowMomentDate);
            if (!rows.has(rowTimestamp)) {
                rows.set(rowTimestamp, []);
            }
            (_rows_get = rows.get(rowTimestamp)) === null || _rows_get === void 0 ? void 0 : _rows_get.push({
                timestamp: translatedTimeSeriesResource.timestamp,
                timeSeriesResources: translatedTimeSeriesResource
            });
        });
        const reversedRows = new Map([
            ...rows.entries()
        ].sort((a, b)=>b[0] - a[0]));
        return {
            rows: reversedRows
        };
    }
    renderRelatedData(values, left, type, reading) {
        const isOverlapingInjections = values.fastActing !== undefined && values.longActing !== undefined;
        return /*#__PURE__*/ _jsx("div", {
            style: {
                left: `${left}%`,
                zIndex: ZINDEX_ORDER.Marker
            },
            className: styles.relatedData__valueWrapper,
            children: isOverlapingInjections ? /*#__PURE__*/ _jsx(Marker, {
                type: "overlapingInjections",
                values: [
                    values.fastActing,
                    values.longActing
                ]
            }, "related-data-at-overlapingInjections") : map(values, (value, index)=>{
                if (!value) {
                    return null;
                }
                return /*#__PURE__*/ _jsx(Marker, {
                    type: index,
                    value: value
                }, `related-data-at-${index}`);
            })
        }, `related-data-${type}-${reading.timestamp}-${reading.deviceSerialNumberToken}`);
    }
    renderReading(reading, left) {
        const { conversion, standards, direction } = this.props;
        const horizontal = direction === 'ltr' ? 'left' : 'right';
        return /*#__PURE__*/ _jsxs("div", {
            style: {
                [horizontal]: `${left}%`,
                zIndex: ZINDEX_ORDER.Marker
            },
            className: styles.reading__valueWrapper,
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: styles.reading__valueWrapper__date,
                    children: moment.unix(reading.timestamp).utc().format('LT')
                }),
                /*#__PURE__*/ _jsx(Marker, {
                    type: "reading",
                    className: styles.reading__valueWrapper__marker,
                    reading: reading,
                    standards: standards,
                    conversion: conversion
                })
            ]
        }, `reading-daily-${reading.timestamp}-${reading.deviceSerialNumberToken}`);
    }
    renderStackedReadingsTime(readings, start, diff) {
        if (readings.length === 2) {
            const [firstReading, secondReading] = readings;
            const firstTimeFromStart = firstReading.timestamp - start;
            const firstAtTimeLeft = (firstTimeFromStart / diff * 100).toFixed(2);
            const firstAtTime = moment.unix(firstReading.timestamp).utc().format('LT');
            const lastTimeFromStart = secondReading.timestamp - start;
            const lastAtTimeLeft = (lastTimeFromStart / diff * 100).toFixed(2);
            const lastAtTime = moment.unix(secondReading.timestamp).utc().format('LT');
            return /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: styles.reading__valueWrapper__date,
                        style: {
                            left: `${firstAtTimeLeft}%`,
                            top: -13
                        },
                        children: firstAtTime
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: styles.reading__valueWrapper__date,
                        style: {
                            left: `${lastAtTimeLeft}%`,
                            top: -26
                        },
                        children: lastAtTime
                    })
                ]
            });
        }
        const minTimestamp = minBy(readings, 'timestamp').timestamp;
        const maxTimesamp = maxBy(readings, 'timestamp').timestamp;
        const timestamp = (minTimestamp + maxTimesamp) / 2;
        const timeFromStart = timestamp - start;
        const atTime = moment.unix(timestamp).utc().format('LT');
        const atTimeLeft = (timeFromStart / diff * 100).toFixed(2);
        return /*#__PURE__*/ _jsx("div", {
            className: styles.reading__valueWrapper__date,
            style: {
                left: `${atTimeLeft}%`,
                top: -26
            },
            children: /*#__PURE__*/ _jsx(FormattedMessage, _object_spread_props(_object_spread({}, messages.labels.nReadingsAt), {
                values: {
                    numberOfReadings: readings.length,
                    atTime,
                    br: /*#__PURE__*/ _jsx("br", {})
                }
            }))
        });
    }
    renderStackedReadings(readings, start, diff) {
        const { conversion, standards, carbsConversion } = this.props;
        return /*#__PURE__*/ _jsxs("div", {
            className: styles.readingsStack,
            children: [
                this.renderStackedReadingsTime(readings, start, diff),
                map(readings, (reading)=>{
                    const readingTimeFromStart = reading.timestamp - start;
                    const left = (readingTimeFromStart / diff * 100).toFixed(2);
                    return /*#__PURE__*/ _jsx("div", {
                        style: {
                            left: `${left}%`,
                            zIndex: ZINDEX_ORDER.Marker
                        },
                        className: styles.reading__valueWrapper,
                        children: /*#__PURE__*/ _jsx(Marker, {
                            type: "reading",
                            className: styles.reading__valueWrapper__marker,
                            reading: reading,
                            standards: standards,
                            conversion: conversion,
                            carbsConversion: carbsConversion
                        })
                    }, `reading-daily-${reading.timestamp}-${reading.deviceSerialNumberToken}`);
                })
            ]
        });
    }
    renderSummary(row, isFirstDay) {
        const { conversion, carbsConversion } = this.props;
        let activityValues = 0;
        let foodValues = 0;
        let medicationFastValues = 0;
        let medicationLongValues = 0;
        let sumValue = 0;
        const rowWithReadings = filter(row, (item)=>!!item.readingId || isManualReading(item));
        row.forEach((item)=>{
            sumValue += item.value ? conversion.toDisplay(item.value) : 0;
            if (item.relatedData) {
                const { activityValue, foodValue, medicationFastValue, medicationLongValue } = calculateRelatedValue(item.relatedData);
                activityValues += activityValue;
                foodValues += foodValue;
                medicationFastValues += medicationFastValue;
                medicationLongValues += medicationLongValue;
            }
            if (item.timeSeriesResources) {
                const { activityValue, foodValue, medicationFastValue, medicationLongValue } = item.timeSeriesResources;
                activityValues += activityValue || 0;
                foodValues += foodValue || 0;
                medicationFastValues += medicationFastValue || 0;
                medicationLongValues += medicationLongValue || 0;
            }
        });
        const averageValue = rowWithReadings.length ? (sumValue / rowWithReadings.length).toFixed(conversion.unit === 'MG_DL' ? 0 : 2) : 0;
        const relatedValues = {
            averageValue,
            activityValues,
            foodValues: carbsConversion.toDisplay(foodValues),
            medicationFastValues,
            medicationLongValues
        };
        return /*#__PURE__*/ _jsx(Summaries, {
            isFirstDay: isFirstDay,
            relatedValues: relatedValues,
            conversion: conversion
        });
    }
    getRelatedDataRowValue(row, type) {
        const { carbsConversion } = this.props;
        let { activityValue, foodValue, medicationFastValue, medicationLongValue } = calculateRelatedValue(row.relatedData || {});
        if (row.timeSeriesResources) {
            const { activityValue: tsrActivityValue, foodValue: tsrFoodValue, medicationFastValue: tsrMedicationFastValue, medicationLongValue: tsrMedicationLongValue } = row.timeSeriesResources;
            activityValue += tsrActivityValue || 0;
            foodValue += tsrFoodValue || 0;
            medicationFastValue += tsrMedicationFastValue || 0;
            medicationLongValue += tsrMedicationLongValue || 0;
        }
        let result = {};
        switch(type){
            case 'foods':
                if (foodValue) {
                    result.food = carbsConversion.toDisplay(foodValue);
                }
                break;
            case 'medications':
                if (medicationFastValue) {
                    result.fastActing = medicationFastValue;
                }
                if (medicationLongValue) {
                    result.longActing = medicationLongValue;
                }
                break;
            case 'activities':
                if (activityValue) {
                    result.activity = activityValue;
                }
                break;
            default:
                result = null;
        }
        return result;
    }
    renderRelatedDataRow(row, timestamp, type) {
        const start = timestamp;
        const end = moment.unix(timestamp).add(1, 'days').unix();
        const diff = end - start;
        return /*#__PURE__*/ _jsx("div", {
            className: styles.row__wrapper,
            children: /*#__PURE__*/ _jsx("div", {
                className: styles.row,
                children: map(row, (data)=>{
                    const timeFromStart = data.timestamp - start;
                    const percentageOnChart = (timeFromStart / diff * 100).toFixed(2);
                    if (data.relatedData || data.timeSeriesResources) {
                        const relatedDataValue = this.getRelatedDataRowValue(data, type);
                        if (!relatedDataValue || !Object.keys(relatedDataValue).length) {
                            return null;
                        }
                        return this.renderRelatedData(relatedDataValue, percentageOnChart, type, data);
                    }
                    return null;
                })
            })
        });
    }
    renderReadingsRow(stackedReadings, start, diff) {
        return stackedReadings.length ? /*#__PURE__*/ _jsx("div", {
            className: styles.row__wrapper,
            children: /*#__PURE__*/ _jsx("div", {
                className: styles.row,
                children: map(stackedReadings, (data)=>{
                    if (data.length === 1) {
                        const timeFromStart = data[0].timestamp - start;
                        const percentageOnChart = (timeFromStart / diff * 100).toFixed(2);
                        return this.renderReading(data[0], percentageOnChart);
                    }
                    return this.renderStackedReadings(data, start, diff);
                })
            })
        }) : this.renderEmptyReadingsRow();
    }
    renderEmptyReadingsRow() {
        return /*#__PURE__*/ _jsx("div", {
            className: styles.row__wrapper,
            children: /*#__PURE__*/ _jsx("div", {
                className: styles.row
            })
        });
    }
    renderDay(row, timestamp, isFirstDay) {
        const { conversion, carbsConversion } = this.props;
        const start = timestamp;
        const end = moment.unix(timestamp).add(1, 'days').unix();
        const diff = end - start;
        const secondsInHour = 3600;
        const stackedReadings = [];
        const sortedRows = row.sort((a, b)=>a.timestamp - b.timestamp);
        forEach(sortedRows, (rowData)=>{
            if (rowData.readingId || isManualReading(rowData)) {
                const rawReadingsRow = omit(rowData, 'relatedData');
                if (!Array.isArray(stackedReadings[0])) {
                    stackedReadings.push([
                        rawReadingsRow
                    ]);
                    return;
                }
                const possibleStack = find(stackedReadings, (stackedRowsArray)=>rawReadingsRow.timestamp - first(stackedRowsArray).timestamp < secondsInHour * 2);
                if (possibleStack) {
                    possibleStack.push(rawReadingsRow);
                    return;
                }
                stackedReadings.push([
                    rawReadingsRow
                ]);
            }
        });
        return /*#__PURE__*/ _jsxs("div", {
            className: styles.day,
            children: [
                /*#__PURE__*/ _jsx(Labels, {
                    dayTimestamp: timestamp,
                    conversion: conversion,
                    carbsConversion: carbsConversion,
                    isFirstDay: isFirstDay
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: styles.day__container,
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: styles.day__wrapper,
                        children: [
                            this.renderReadingsRow(stackedReadings, start, diff),
                            this.renderRelatedDataRow(row, timestamp, 'foods'),
                            this.renderRelatedDataRow(row, timestamp, 'medications'),
                            this.renderRelatedDataRow(row, timestamp, 'activities')
                        ]
                    })
                }),
                this.renderSummary(row, isFirstDay)
            ]
        }, `day-${timestamp}`);
    }
    render() {
        const { rows } = this.state;
        let index = 0;
        return /*#__PURE__*/ _jsx("div", {
            className: cn(styles.wrapper),
            children: Array.from(rows.entries()).map(([timestamp, row])=>this.renderDay(row, timestamp, index++ === 0))
        });
    }
    constructor(props){
        super(props);
        this.state = {
            rows: []
        };
    }
}
_define_property(DailyChart, "propTypes", {
    // Explicit props
    startDayTimestamp: PropTypes.string.isRequired,
    conversion: PropTypes.object.isRequired,
    direction: PropTypes.string.isRequired,
    carbsConversion: PropTypes.object.isRequired,
    standards: PropTypes.shape({
        maxValue: PropTypes.number.isRequired,
        preMeal: PropTypes.shape({
            highThreshold: PropTypes.number.isRequired,
            lowThreshold: PropTypes.number.isRequired
        }),
        postMeal: PropTypes.shape({
            highThreshold: PropTypes.number.isRequired,
            lowThreshold: PropTypes.number.isRequired
        })
    }),
    readingsWithRelatedData: PropTypes.array,
    translatedTimeSeriesResources: PropTypes.array
});
export default withStyles(styles)(DailyChart);
